import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Link,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get invitation data from location state or query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    if (token) {
      // Fetch invitation details
      fetchInvitationDetails(token);
    }

    if (email) {
      setFormData(prev => ({ ...prev, email }));
    }
  }, [location]);

  const fetchInvitationDetails = async (token) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/crew/invitations/${token}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid or expired invitation');
      }
      const data = await response.json();
      console.log('Invitation details:', data);
      setInvitationData({ ...data, token });
      
      // Pre-fill email if provided in invitation
      if (data.email) {
        setFormData(prev => ({ ...prev, email: data.email }));
      }
    } catch (error) {
      console.error('Error fetching invitation:', error);
      setErrors({ global: error.message || 'Invalid or expired invitation link' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear field-specific error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // If this is an invitation for a new user, accept it with the password
      if (invitationData?.token) {
        console.log('Accepting invitation with token:', invitationData.token);
        const acceptResponse = await fetch(`${API_BASE}/api/v1/crew/invitations/${invitationData.token}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email, // Add email to ensure it matches the invitation
            password: formData.password
          })
        });

        if (!acceptResponse.ok) {
          const errorData = await acceptResponse.json();
          console.error('Failed to accept invitation:', errorData);
          throw new Error(errorData.message || 'Failed to accept invitation');
        }

        const acceptData = await acceptResponse.json();
        console.log('Invitation accepted:', acceptData);

        // Login with the new credentials
        const loginResponse = await fetch(`${API_BASE}/api/v1/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          })
        });

        if (!loginResponse.ok) {
          const errorData = await loginResponse.json();
          console.error('Failed to login after registration:', errorData);
          throw new Error(errorData.message || 'Failed to login after registration');
        }

        const loginData = await loginResponse.json();
        
        // Store auth token
        localStorage.setItem('token', loginData.token);
        
        // Navigate to the project
        navigate(`/projects/${acceptData.projectId}`);
        return;
      }

      // If no invitation, proceed with normal registration
      const registerResponse = await fetch(`${API_BASE}/api/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password
        })
      });

      if (!registerResponse.ok) {
        const errorData = await registerResponse.json();
        throw new Error(errorData.message || 'Registration failed');
      }

      const userData = await registerResponse.json();
      console.log('Registration successful:', userData);

      // Store token and go to dashboard
      localStorage.setItem('token', userData.token);
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ global: error.message || 'Registration failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      background: (theme) => `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`
    }}>
      <Container component="main" maxWidth="sm">
        <Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography component="h1" variant="h5" gutterBottom>
              {invitationData ? 'Accept Invitation & Register' : 'Create your account'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please fill out the form below to continue
            </Typography>
          </Box>

          {invitationData && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You've been invited to join {invitationData.projectName} as a {invitationData.role}
            </Alert>
          )}

          {errors.global && (
            <Alert severity="error" sx={{ mb: 2 }} aria-live="polite">
              {errors.global}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              autoComplete="name"
              autoFocus
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              error={!!errors.name}
              helperText={errors.name}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              disabled={Boolean(invitationData?.email) || loading}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              error={!!errors.password}
              helperText={errors.password || 'Password must be at least 6 characters'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={loading}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                      onClick={() => setShowConfirmPassword((s) => !s)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : invitationData ? (
                'Accept & Register'
              ) : (
                'Register'
              )}
            </Button>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Already have an account?
              </Typography>
              <Link component="button" type="button" onClick={() => navigate('/login')} variant="body2">
                Sign in
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Register;
